/** Part II — On the ground: moving, maps, water, weather and the wild, and fighting. */
import type { HelpPart } from './types.js';

export const PART_GROUND: HelpPart = {
  numeral: 'II',
  title: 'On the ground',
  chapters: [
    {
      id: 'moving',
      title: 'Moving',
      blurb: 'Walking, running, climbing, the camera, and getting about in water and wind.',
      pages: [
        {
          id: 'moving/walk',
          title: 'Walking and running',
          terms: ['wasd', 'sprint', 'mouse look', 'pointer lock', 'speed'],
          body: `<p>{{key:moveForward}} {{key:moveLeft}} {{key:moveBack}} {{key:moveRight}} move you relative to where you
          look. Click the view to capture the mouse for looking around; <kbd>Esc</kbd> releases it.</p>
          <p>You walk at about 6 m/s. Hold {{key:sprint}} to sprint at 9 m/s. Crouched you move at 3 m/s.
          Slopes, steps and walls are solid; small steps you simply walk up.</p>
          <p>Your movement is a request the server carries out at a fixed speed, so everyone sees you where you really
          are. On a slow connection you may feel a small correction now and then — that is the server and your game
          agreeing.</p>`,
          see: ['moving/autorun', 'moving/jump'],
        },
        {
          id: 'moving/autorun',
          title: 'Auto-run',
          terms: ['autorun', 'auto run', 'keep walking'],
          body: `<p>Press {{key:autoRun}} (on a controller, d-pad left) to keep walking forward without holding a key. Steer with the mouse;
          {{key:sprint}} still sprints. Any movement key, swimming, sitting down, mounting a vehicle, a cutscene, the placement map or
          {{key:autoRun}} again stops it. An <b>Auto-run</b> chip shows while it is on.</p>`,
        },
        {
          id: 'moving/jump',
          title: 'Jumping, climbing and crouching',
          terms: ['mantle', 'ledge', 'crouch', 'duck'],
          body: `<p>{{key:jump}} jumps. Let go early for a short hop; hold it for the full arc.</p>
          <p><b>Climbing a ledge</b>: jump while pushing into an obstacle between knee and shoulder height (about
          0.7 to 1.45 m) with room on top, and you pull yourself up onto it instead.</p>
          <p><b>Crouching</b>: {{key:crouch}} toggles a crouch (when you are not sprinting). You can crawl under low
          things; you stand up again only where there is room. Sprinting, jumping or dashing stands you up.</p>`,
        },
        {
          id: 'moving/dash',
          title: 'Dash and slide',
          terms: ['dodge', 'double tap', 'slide', 'i-frames'],
          body: `<p><b>Dash</b>: tap a movement key twice quickly. You burst a few metres that way. The first moment of a
          dash dodges a blow that lands during it. A dash needs about a second to recharge, and you cannot dash with your
          guard up, while staggered, or in water.</p>
          <p><b>Slide</b>: while sprinting, press {{key:crouch}}. You slide low and fast, slowing to a walk. You cannot
          attack during the first part of a slide.</p>`,
          see: ['combat/defend'],
        },
        {
          id: 'moving/camera',
          title: 'First and third person',
          terms: ['camera', 'scroll wheel', 'zoom', 'third person'],
          body: `<p>Scroll the mouse wheel out to step behind your character (third person, up to 8 m back); scroll back in
          to return to first person. The camera pulls in rather than pass through a wall or a hill.</p>
          <p>On a hovercraft the wheel sets the chase distance (3 to 45 m). Options → Controls → <b>Camera motion</b>
          softens or turns off camera shake, kicks and sway.</p>`,
          see: ['options/comfort'],
        },
        {
          id: 'moving/swim',
          title: 'Swimming and diving',
          terms: ['swim', 'dive', 'breath', 'underwater', 'wade'],
          body: `<p>Walk into water and you wade, slower the deeper it gets. Past chest depth you float and swim, holding
          yourself at the surface.</p>
          <ul>
          <li>Hold {{key:jump}} to rise; hold {{key:crouch}} to dive. Look down and swim forward to dive along your view.</li>
          <li>Under the surface a <b>breath meter</b> appears. You hold about a minute of breath. When it runs out you are
          carried up to the surface — you never drown. Breath refills three times as fast as it runs out.</li>
          <li>Every stroke rings the water around you and you hear it — a steady crawl of strokes, never a footstep.
          Other swimmers ring the water and are heard the same way.</li>
          <li>Climbing out of deep water you hear the water run off you, and you stay <b>wet</b> for a while: see
          <a data-page="water/wet">Getting out wet</a>.</li>
          </ul>
          <p>Townsfolk swim too: you will see them float rather than walk the bottom.</p>`,
          see: ['water/water', 'water/wet', 'water/fishing'],
        },
        {
          id: 'moving/wind',
          title: 'Wind and footing',
          terms: ['wind', 'storm', 'slow'],
          body: `<p>A strong head wind slows you a little — you lean into it. A sandstorm slows you more. Storms can also
          tire you out the longer you stay in them; see <a data-page="weather/hazards">Storm hazards</a>.</p>`,
        },
      ],
    },
    {
      id: 'maps',
      title: 'Maps and finding your way',
      blurb: 'The local map, the World Atlas and its layers, search, and waypoints.',
      pages: [
        {
          id: 'maps/local',
          title: 'The local map',
          terms: ['m', 'minimap', 'overlay', 'ctrl m', 'radar'],
          body: `<p><kbd>M</kbd> opens the local map: your surroundings, the people and creatures around you, buildings
          and their names, player-city borders, shuttles and your waypoints. <kbd>Ctrl</kbd>+<kbd>M</kbd> lays the same
          map over the view while you walk.</p>
          <p>Right-click anywhere on it to drop a waypoint. Live events in the area — a festival, a feast, a rite —
          show as markers; click one to set a waypoint to it.</p>`,
          see: ['maps/waypoints'],
        },
        {
          id: 'maps/atlas',
          title: 'The World Atlas',
          terms: ['n', 'world map', 'atlas', 'zoom', 'buildings'],
          body: `<p><kbd>N</kbd> opens the World Atlas, the whole continent. Drag to pan, scroll to zoom. Zoom in far
          enough and towns resolve into streets and buildings, each named for what it is — a clinic, a cantina, a
          granary, a residential tower. Hover a building for its name, size and bearing; click to pin it.</p>
          <p>Settlements are dots coloured by their people; player cities are squares with their borders; battles are red
          dots (pulsing while the fight is on — you can go there).</p>
          <p>The map opens fitted to the whole world and fills in quickly as you move; tiles you have seen stay sharp.</p>`,
          see: ['maps/layers', 'maps/find'],
        },
        {
          id: 'maps/layers',
          title: 'Map layers',
          terms: ['temperature', 'civilisation layer', 'war layer', 'people', 'economy', 'trade routes', 'guild layer'],
          body: `<p>The Atlas toolbar switches what is drawn over the land:</p>
          <ul>
          <li><b>Terrain</b> — the ground itself, forests tinted green.</li>
          <li><b>Weather</b> layers — temperature and the like, drawn from the live weather.</li>
          <li><b>Civilisation</b> — which people holds each region.</li>
          <li><b>War</b> — fronts and battles.</li>
          <li><b>People</b> — a heat map of where townsfolk are, with online travelers as named diamonds.</li>
          <li><b>Economy</b> — where the credits are (gold) and where the goods are (teal); white where a town is rich in
          both.</li>
          <li><b>Trade</b> — the roads, the habits of trade between towns, and live haulers and cargo flights on their
          way, drawn moving.</li>
          <li><b>Guilds</b> (⚜) — guild land as discs, trade guilds dashed.</li>
          </ul>`,
        },
        {
          id: 'maps/find',
          title: 'Finding a place or a building',
          terms: ['find', 'search map', 'clinic', 'nearest'],
          body: `<p>The Atlas's <b>Find</b> tab searches every named place in the world — buildings, landmarks, gates,
          ports, cemeteries, venues, player structures, guild halls and player cities. Type a few letters of any word
          (“clin”, “armory aur”). Narrow by category chips, by people, by distance (anywhere, or within 1, 5, 25 or
          100 km of you, or in view) and sort by nearest, name or size.</p>
          <p>Each result offers <b>⌖ show</b> (fly the map there) and <b>◆ waypoint</b> (set a waypoint and follow
          it). <b>Show on map</b> marks every match at once.</p>`,
        },
        {
          id: 'maps/waypoints',
          title: 'Waypoints and the heading strip',
          terms: ['waypoint', 'follow', 'arrived', 'heading', 'marker'],
          body: `<p>Right-click either map to set a named waypoint (a gold ◆). Right-click a waypoint to rename it; clear
          the name to delete it. Waypoints belong to the planet you set them on, are yours alone, last between sessions,
          and you can keep 64.</p>
          <p>The waypoint you set most recently is <b>followed</b>: a strip under your wallet shows an arrow turning with
          your view (“ahead”, “ahead right”, “behind”…) and the distance; in the world a marker stands over it, or an
          arrow at the screen edge points to it. Inside 12 m it says <b>Arrived</b>. Click a waypoint on a map to follow
          that one instead; ✕ on the strip stops following.</p>`,
        },
      ],
    },
    {
      id: 'water',
      title: 'Water',
      blurb: 'Seas, lakes and rivers, what disturbs them, and fishing.',
      pages: [
        {
          id: 'water/water',
          title: 'Water that answers back',
          terms: ['ripples', 'splash', 'wake', 'sea state', 'waves', 'rings'],
          body: `<p>The sea, the lakes and the rivers react to what moves through them. Walking in the shallows leaves
          <b>rings</b> at each stride; swimming strokes ring the surface; a body or a craft hitting the water splashes,
          harder the faster it lands. Creatures wading, the rain and the plunge pools under waterfalls all disturb it
          too.</p>
          <p>The wind sets the <b>sea state</b>: calm days are glassy, a gale builds whitecaps and longer swells, and the
          direction of the waves follows the weather.</p>
          <p>What else answers: <a data-page="water/shots">shots and blasts</a> that land in water,
          <a data-page="water/craft">craft over the water</a>, and the way you stay
          <a data-page="water/wet">wet</a> after a swim.</p>`,
          see: ['moving/swim', 'water/shots', 'water/craft', 'water/wet', 'water/detail'],
        },
        {
          id: 'water/shots',
          title: 'Shots and blasts into water',
          terms: ['shoot water', 'plop', 'hiss', 'steam', 'cannon'],
          body: `<p>A shot that meets water ends there. It does not fly on to hit the lake bed or leave a mark on the
          ground beneath.</p>
          <ul>
          <li>A bullet or slug lands with a <b>plop</b> and a small splash. Energy bolts <b>hiss</b> and throw a little
          steam. Heavier rounds splash bigger: pellets least, a slug more, a shell most.</li>
          <li>Every hit leaves a ring spreading on the surface.</li>
          <li>Someone swimming is still hit by a shot aimed at them; the water marks where the shot went in.</li>
          <li>A ship's cannon fired into the sea throws up a tall column of spray and a wide ring, with a hiss and a
          deep thump you hear a moment later if it is far away.</li>
          </ul>
          <p>Ships shot down over water burst at the surface instead of on the bottom, and a wreck that sinks does not
          keep smoking under the lake.</p>`,
          see: ['combat/weapons', 'water/craft'],
        },
        {
          id: 'water/craft',
          title: 'Craft over the water',
          terms: ['spray', 'downwash', 'skim', 'breach', 'hover over water'],
          body: `<p><b>Hovercraft.</b> Over open water a hovercraft throws <b>spray</b> instead of dust and drags a wake
          behind it. Even at rest its cushion hisses on the surface; the hiss rises as you pick up speed. Other riders'
          craft do the same, and you hear them pass.</p>
          <p><b>Ships.</b> Fly a ship low over the water and its engines blow the surface flat beneath it — the
          <b>downwash</b>: a ring of spray under the hull, rings spreading out, and a roar that grows the lower and
          harder you fly. Bigger hulls disturb the water from higher up.</p>
          <ul>
          <li>You cannot fly a ship under the water. Hold it down and the belly <b>skims</b> the surface; skimming drags
          you to a slower speed.</li>
          <li>The moment the hull first touches the water it <b>breaches</b> — one big splash. Hitting the water hard,
          coming straight down fast, damages the ship like hitting the ground.</li>
          </ul>
          <p>The same is true of everyone else's craft: townsfolk travelling by air, war ships and cargo freighters
          raise downwash when they fly low over a lake or the sea, and craft that cross water skim its surface rather
          than its bed.</p>`,
          see: ['vehicles/hovercraft', 'vehicles/ship', 'water/water'],
        },
        {
          id: 'water/wet',
          title: 'Getting out wet',
          terms: ['wet', 'drip', 'wet footprints', 'climb out', 'soaked'],
          body: `<p>Swim, dive, or wade deeper than about 0.6 m and you get <b>soaked</b> — the deeper, the wetter. Step out
          of deep water and three things happen:</p>
          <ul>
          <li>You hear the water run off you as you climb out.</li>
          <li>Water <b>drips</b> off you for a few seconds, tapering off.</li>
          <li>For about 20 seconds you leave darker <b>wet footprints</b> that dry and fade on their own. The wetter you
          were, the longer they last.</li>
          </ul>
          <p>Walking back into deep water stops the dripping. Other travelers and townsfolk leave wet prints too when
          they come out of the water. Nothing drips while you ride, fly or are indoors.</p>`,
          see: ['moving/swim', 'water/water'],
        },
        {
          id: 'water/detail',
          title: 'Water effects and your graphics settings',
          terms: ['fluid physics', 'water particles', 'splash settings'],
          body: `<p>Two settings decide how much of this you see; the sounds play on every setting.</p>
          <ul>
          <li><b>Realistic fluid physics</b> (Options → Graphics) draws the rings: strides, strokes, wakes, shots and
          rain on the surface. It is on from the Low preset up.</li>
          <li><b>Particle density</b> draws the splashes, spray and drips. Minimal never draws them. Potato draws none
          by default, but if you raise its particle density, splashes appear while its rings stay off.</li>
          </ul>
          <p>Low and above show both, with more spray and drips the higher the particle density.</p>`,
          see: ['options/graphics', 'water/water'],
        },
        {
          id: 'water/fishing',
          title: 'Fishing',
          terms: ['fish', 'cast', 'reel', 'bobber', 'catch'],
          body: `<p>Walk up to the edge of a sea, lake, river or pond and the prompt offers <b>{{key:interact}} — cast a
          line</b>. Cast, then wait: the bobber floats, and when it dips something has taken the bait.</p>
          <p><b>The reel</b>: a bar appears with a catch zone and a fish. Hold {{key:interact}} to lift the zone; let go
          and it sinks. Keep the fish inside the zone and the meter fills; let it slip out and the meter drains. A full
          meter lands the fish, an empty one loses it. Fighters dart further and more often.</p>
          <p>Pressing {{key:interact}} before a bite brings the line in empty. Which fish bites depends on the water
          (sea, lake, river), the hour, the weather and the land around it; most are small, a few are big. A landed fish
          goes into your resources as food with its length, and into the Field Guide.</p>`,
          see: ['weather/guide'],
        },
      ],
    },
    {
      id: 'weather',
      title: 'Weather and the wild',
      blurb: 'Rain, snow and storms, what they do to you, and the living things beyond the walls.',
      pages: [
        {
          id: 'weather/weather',
          title: 'Weather',
          terms: ['rain', 'snow', 'sandstorm', 'fog', 'thunder', 'clouds'],
          body: `<p>Weather moves across the continent as fronts: the sky greys over first, then it rains, and the rain
          thins before the sky clears. Each biome has its own climate — snow in the cold highlands, sandstorms only in the
          dune desert, mist over the wetlands.</p>
          <ul>
          <li><b>Rain</b> wets the ground (puddles fill in hollows), rings the water and drums on roofs; shelter keeps
          the ground under it dry.</li>
          <li><b>Thunderstorms</b> flash, and the thunder arrives after a delay that tells you how far away the strike
          was — about three seconds per kilometre.</li>
          <li><b>Snow</b> settles on the ground and on buildings; footprints press into it.</li>
          <li><b>Sandstorms</b> fill the air with blown sand and a tan haze a few hundred metres deep.</li>
          </ul>
          <p>The Town window's <b>Weather</b> tab is the <b>forecast</b> where you stand: now, in an hour, in three and in
          six. When the weather office is holding the sky over the Commons (a set weather for the day), the tab says so
          instead of guessing. Off the Commons there is no forecast.</p>`,
          see: ['weather/hazards'],
        },
        {
          id: 'weather/hazards',
          title: 'Storm hazards',
          terms: ['storm', 'blizzard', 'exposure', 'shelter', 'lightning'],
          body: `<p>Staying out in a thunderstorm, a blizzard or a sandstorm builds <b>exposure</b>, shown on a chip
          at the top of the screen:</p>
          <ul>
          <li>Exposure slows you, never below about two-thirds of your speed.</li>
          <li>A blizzard chills you: you cannot sprint until you warm up.</li>
          <li>A sandstorm blinds: the edges of your view fill with blowing sand.</li>
          <li>In a thunderstorm, lightning can strike close by — a crack, a scorch mark and a stumble, never harm.</li>
          </ul>
          <p>Go indoors, into a cave, or sit by a fire and the exposure falls away. Storms never hurt your health.</p>`,
          see: ['town/camps'],
        },
        {
          id: 'weather/wildlife',
          title: 'Wildlife',
          terms: ['herds', 'birds', 'butterflies', 'fireflies', 'crabs', 'dragonflies', 'animals'],
          body: `<p>The open country is alive. Herds graze where their land is open — copperhorn on the plains, fenback in
          the wetlands, woolcrag in the highlands. They look up as you approach and run if you come too close, then
          drift back.</p>
          <p>Birds — jays, gulls, hawks and starlings — perch in trees, on roofs and on parked ships, forage on the
          ground, drink at shores and take off when someone comes near. Flocks pass overhead and migrate with the
          seasons. In the woods gnats gather by day, moths at dusk and fireflies at night. Butterflies work the
          meadows, dragonflies hunt over still water, and crabs scuttle sideways on the beaches.</p>
          <p>None of these will hurt you. The creatures of the wilds are another matter.</p>`,
          see: ['weather/creatures', 'weather/guide'],
        },
        {
          id: 'weather/creatures',
          title: 'Creatures and dens',
          terms: ['beasts', 'stalker', 'wyrm', 'shrike', 'drone', 'brute', 'lurker', 'den'],
          body: `<p>Each biome has its own dangerous creatures — stalkers, drones, brutes, lurkers, wyrms and shrikes —
          living around <b>dens</b> in the wilds and the deep woods. A nameplate over each one gives its name, nature and
          health.</p>
          <p>They wind up before they strike (see <a data-page="combat/telegraphs">Reading a blow</a>). Only two will
          press you at once; the rest circle. Killing them pays loot and counts toward <b>cull contracts</b>. Creatures
          also fight townsfolk who wander too far.</p>`,
          see: ['combat/weapons'],
        },
        {
          id: 'weather/guide',
          title: 'The Field Guide',
          terms: ['field guide', 'journal', 'species', 'collection'],
          body: `<p>The <b>Guide</b> button opens your Field Guide: four books — <b>birds</b>, <b>insects</b>,
          <b>creatures</b> and <b>fish</b>. A species is noted the first time you see it up close (birds and insects), hunt
          one (creatures) or land one (fish), with where and when.</p>
          <p>Complete a book and it pays <b>250 ⓒ</b>, once, and earns a permanent line in the Memory Book.</p>`,
        },
      ],
    },
    {
      id: 'combat',
      title: 'Fighting',
      blurb: 'Weapons, attacking and defending, reading a blow, and where player fights are allowed.',
      pages: [
        {
          id: 'combat/weapons',
          title: 'Weapons and ammunition',
          terms: ['gun', 'blade', 'magazine', 'capacitor', 'reload', 'energy', 'ballistic'],
          body: `<p>Weapons are crafted, never bought from the game: ten guns and five blades across five tiers, each with its
          own damage, speed and reach. The quality you craft at scales the damage.</p>
          <p>Guns are fed one of two ways:</p>
          <ul>
          <li><b>Magazine</b> (ballistic guns) — a set number of rounds; the last round starts a reload by itself, and
          <kbd>R</kbd> reloads a part-empty magazine.</li>
          <li><b>Capacitor</b> (energy guns) — each shot draws charge that refills over time. Sustained fire drains it
          faster than it refills; pace your shots.</li>
          </ul>
          <p>The action bar shows rounds, the reload sweep or the charge. Every shot shows a tracer and leaves a mark where
          it lands — a hole, a scorch — that fades after a while.</p>`,
          see: ['inventory/crafting'],
        },
        {
          id: 'combat/attack',
          title: 'Attacking',
          terms: ['attack', 'strike', 'aim', 'reticle', 'cooldown', 'weapon ring'],
          body: `<p>{{key:attack}}, or a click with the mouse captured, attacks. A soft ring on the screen marks what your
          attack will hit — the target inside your weapon's reach and facing cone.</p>
          <p>Each weapon has a cooldown. A blow that <b>lands</b> shortens the next cooldown; a <b>miss</b> lengthens
          it — swinging at nothing costs you. {{key:weaponNext}} walks your weapons (Shift with it goes back;
          <kbd>Alt</kbd>+wheel too), bare hands included.</p>
          <p>Heavy blows <b>stagger</b>: a staggered enemy's wind-up is cancelled if it was early, and a staggered you moves
          at half speed and cannot attack for a moment.</p>`,
        },
        {
          id: 'combat/defend',
          title: 'Guard, parry and dodge',
          terms: ['guard', 'parry', 'block', 'dodge', 'right mouse'],
          body: `<ul>
          <li><b>Guard</b> — hold the right mouse button. Blows from in front (about 60° either side) do half damage.
          You walk, never sprint or dash, with your guard up; attacking lowers it.</li>
          <li><b>Parry</b> — raising the guard just before a blow lands opens a short window. A blow inside it is parried:
          no damage, and the attacker staggers. Raising your guard over and over shortens the window; leave it down a
          moment and it recovers.</li>
          <li><b>Dodge</b> — a dash at the right moment (<a data-page="moving/dash">Dash and slide</a>) slips the blow
          entirely.</li>
          </ul>
          <p>A blow from behind cannot be guarded or parried.</p>`,
        },
        {
          id: 'combat/telegraphs',
          title: 'Reading a blow',
          terms: ['telegraph', 'windup', 'threat indicator', 'warning'],
          body: `<p>Creatures and hostile townsfolk <b>wind up</b> before they strike — a ring on the ground, a lean, a
          swell — about one to one and a half seconds. The blow lands where you are when the wind-up ends, so stepping out
          of reach, dodging, parrying or guarding all work.</p>
          <p>When a blow is wound up at you, an arc at the edge of the screen points toward the attacker and brightens as
          the moment nears — useful when it is behind you. A player's swing at you — in a duel, the arena, a bounty
          or a war — shows the same arc as it starts.</p>`,
        },
        {
          id: 'combat/health',
          title: 'Health, defeat and recovery',
          terms: ['health', 'regen', 'defeat', 'respawn', 'downed'],
          body: `<p>Health returns on its own once you have been out of a fight for ten seconds. If it reaches zero you are
          defeated: you wake at the nearest landing pad with a few seconds of protection.</p>
          <p>Rations and salves from the Workbench heal you on the spot.</p>`,
        },
        {
          id: 'combat/townsfolk',
          title: 'Hurting townsfolk',
          terms: ['shoot versona', 'grudge', 'nemesis', 'regard'],
          body: `<p>Townsfolk can be hurt but never killed by a player. A person you hurt does not forget: one shot makes
          them hostile, two a <b>nemesis</b>. They fight back, the town thinks less of you, and a nemesis may pay a
          <a data-page="town/hunters">bounty hunter</a> to come after you.</p>`,
          see: ['people/relations'],
        },
        {
          id: 'combat/pvp',
          title: 'Fighting other players',
          terms: ['pvp', 'player versus player', 'sanctuary', 'consent'],
          body: `<p>Players cannot hurt each other except inside an agreed contest:</p>
          <ul>
          <li><b>Duels</b> and the <b>Relay Skirmish</b> arena (see the next pages);</li>
          <li><b>conflict sectors</b> you enrol in (<code>/enroll</code>);</li>
          <li>a <b>bounty</b> you took, between you and your mark (both ways);</li>
          <li>a <b>ground war</b> between your guild, city or civilisation and another.</li>
          </ul>
          <p>Within 30 m of a landing pad is always a sanctuary, and you are protected for a few seconds after a defeat.
          Some contest types are switched on or off by the server; <code>/pvp</code> in chat tells you what is open.</p>`,
          see: ['combat/duels', 'groups/wars'],
        },
        {
          id: 'combat/duels',
          title: 'Duels and the arena',
          terms: ['duel', 'arena', 'relay skirmish', 'sparring ring', 'yield'],
          body: `<p><b>Duel</b>: <code>/duel name</code> challenges a player; they <code>/accept</code> or
          <code>/decline</code>. Both walk into the town's sparring ring and <code>/ready</code>; a three-second count
          starts it. Leave the ring for three seconds and you forfeit; <code>/yield</code> concedes; at 90 seconds it is a
          draw.</p>
          <p><b>Relay Skirmish</b>: <code>/arena 1</code> to <code>4</code> queues for that team size at the town's
          arena. Teams fight over a relay: hold it uncontested to capture. First team to three wins; a match lasts at
          most fifteen minutes. <code>/leavequeue</code> leaves the queue.</p>
          <p>In contests, weapons use one fair rule set (crafted quality does not count) and health does not regenerate.</p>`,
        },
      ],
    },
  ],
};
